import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleChangeLog } from '@/hooks/useScheduleChangeLog';
import { formatDateFull } from '@/lib/scheduleUtils';

interface Props {
  projectId: string;
}

type LogFilter = 'all' | 'date_changes' | 'delays' | 'client_visible' | 'critical_path' | 'completion';

type FilterConfig = { color: string; matches: (changeType: string, isClientVisible: boolean, isCritical: boolean) => boolean };

const FILTER_MATCHES: Record<LogFilter, FilterConfig> = {
  all: { color: '#1A1A1A', matches: () => true },
  date_changes: { color: '#6B7280', matches: (type) => type === 'item_date_changed' || type === 'item_duration_changed' || type === 'linked_items_recalculated' },
  delays: { color: '#EF4444', matches: (type) => type === 'item_delayed' || type === 'at_risk' },
  client_visible: { color: '#059669', matches: (_, isClientVisible) => isClientVisible },
  critical_path: { color: '#7C3AED', matches: (type, _, isCritical) => isCritical || type === 'critical_path_recalculated' },
  completion: { color: '#059669', matches: (type) => type === 'item_completed' },
};

export default function ScheduleChangeLogPanel({ projectId }: Props) {
  const { t } = useTranslation();
  const { logs, loading } = useScheduleChangeLog(projectId);
  const [activeFilter, setActiveFilter] = useState<LogFilter>('all');

  const FILTER_CONFIG: Record<LogFilter, { label: string; color: string; matches: (changeType: string, isClientVisible: boolean, isCritical: boolean) => boolean }> = {
    all: { label: t('changeLog.filterAll'), color: '#1A1A1A', matches: () => true },
    date_changes: { label: t('changeLog.filterDateChanges'), color: '#6B7280', matches: (type) => type === 'item_date_changed' || type === 'item_duration_changed' || type === 'linked_items_recalculated' },
    delays: { label: t('changeLog.filterDelays'), color: '#EF4444', matches: (type) => type === 'item_delayed' || type === 'at_risk' },
    client_visible: { label: t('changeLog.filterClientVisible'), color: '#059669', matches: (_, isClientVisible) => isClientVisible },
    critical_path: { label: t('changeLog.filterCriticalPath'), color: '#7C3AED', matches: (type, _, isCritical) => isCritical || type === 'critical_path_recalculated' },
    completion: { label: t('changeLog.filterCompletion'), color: '#059669', matches: (type) => type === 'item_completed' },
  };

  const filtered = activeFilter === 'all'
    ? logs
    : logs.filter((log) =>
        FILTER_CONFIG[activeFilter].matches(
          log.change_type,
          log.is_client_visible,
          false
        )
      );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xl"></i>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-[#9CA3AF] text-xs">{t('changeLog.noChanges')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2 mb-2">
        {(Object.keys(FILTER_CONFIG) as LogFilter[]).map((key) => {
          const cfg = FILTER_CONFIG[key];
          const count = key === 'all'
            ? logs.length
            : logs.filter((l) => cfg.matches(l.change_type, l.is_client_visible, false)).length;
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                isActive ? 'text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
              }`}
              style={isActive ? { backgroundColor: cfg.color } : {}}
            >
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Log list */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {filtered.map((log) => (
          <div key={log.id} className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold text-[#1A1A1A]">
                    {log.schedule_item_title || 'Project-wide change'}
                  </p>
                  {log.is_client_visible && (
                    <span className="text-[9px] font-bold text-[#059669] bg-[#F0FDF4] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      {t('changeLog.clientVisible')}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[#9CA3AF] capitalize mt-0.5">
                  {log.change_type.replace(/_/g, ' ')}
                  {log.changed_by_role ? ` · ${log.changed_by_role}` : ''}
                </p>
                {log.change_reason && (
                  <p className="text-[11px] text-[#6B7280] mt-1 bg-[#F9FAFB] rounded-lg px-2 py-1">
                    {t('changeLog.reason')}: {log.change_reason}
                  </p>
                )}
                {log.old_start_date && log.new_start_date && (
                  <p className="text-[10px] text-[#6B7280] mt-1">
                    {formatDateFull(log.old_start_date)} → {formatDateFull(log.new_start_date)}
                    {log.old_end_date && log.new_end_date ? ` · End: ${formatDateFull(log.old_end_date)} → ${formatDateFull(log.new_end_date)}` : ''}
                  </p>
                )}
                {log.affected_item_count > 0 && (
                  <p className="text-[10px] text-[#E8621A] font-medium mt-0.5">
                    {t('changeLog.linkedItemsRecalculated', { n: log.affected_item_count, plural: log.affected_item_count !== 1 ? 's' : '' })}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className="text-[10px] text-[#9CA3AF] whitespace-nowrap">
                  {new Date(log.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                {log.changed_by && (
                  <span className="text-[9px] text-[#9CA3AF] mt-0.5">{log.changed_by}</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-[#9CA3AF] text-xs py-4">{t('changeLog.noMatchFilter')}</p>
        )}
      </div>
    </div>
  );
}